/**
 * BasicInfo フォーム（サイドバー常駐）から
 * 共通入力値（対象年・対象月・生年月日）を読み取り、バリデーションする。
 *
 * Tax タブと Social タブの両方で同じロジックを使うため分離。
 */

import { loadBasicInfoFromLocal } from '../basicInfoMenuApi';
import { parseYenInput } from '../taxNumberInput';

// -----------------------------------------------------------
// 型定義
// -----------------------------------------------------------

export type DobResult =
  | { ok: true; y: number; m: number; d: number }
  | { ok: false; message: string };

function readSelectDigits(id: string, maxLen: number): string {
  const el = document.querySelector<HTMLSelectElement | HTMLInputElement>(id);
  const fromDom = String(el?.value ?? '').replace(/[^0-9]/g, '').slice(0, maxLen);
  if (fromDom) return fromDom;
  // リロード直後など、DOM 復元前でも localStorage を見る
  const local = loadBasicInfoFromLocal();
  if (!local) return '';
  if (id === '#basic-info-year') return local.year;
  if (id === '#basic-info-month') return local.month;
  if (id === '#basic-info-dob-y') return local.dobY;
  if (id === '#basic-info-dob-m') return local.dobM;
  if (id === '#basic-info-dob-d') return local.dobD;
  return '';
}

// -----------------------------------------------------------
// 公開関数
// -----------------------------------------------------------

/** BasicInfo フォームから生年月日を読み取り、バリデーション結果を返す */
export function readDobFromBasicInfo(): DobResult {
  const y = parseYenInput(readSelectDigits('#basic-info-dob-y', 4));
  const m = parseYenInput(readSelectDigits('#basic-info-dob-m', 2));
  const d = parseYenInput(readSelectDigits('#basic-info-dob-d', 2));

  if (y === null || m === null || d === null) {
    return { ok: false, message: '生年月日（年・月・日）をすべて入力してね。' };
  }
  if (y < 1900 || y > 2100) return { ok: false, message: '生年は 1900〜2100 の整数で入力してね。' };
  if (m < 1 || m > 12) return { ok: false, message: '生月は 1〜12 の整数で入力してね。' };
  if (d < 1 || d > 31) return { ok: false, message: '生日は 1〜31 の整数で入力してね。' };

  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return { ok: false, message: '存在しない日付です。生年月日を確認してね。' };
  }

  return { ok: true, y, m, d };
}

/** BasicInfo フォームから対象年を読み取り、エラーなら文字列を返す */
export function readYearFromBasicInfo(): number | string {
  const year = parseYenInput(readSelectDigits('#basic-info-year', 4));
  if (year === null) return '対象年を入力してね。';
  if (year < 2020 || year > 2100) return '対象年は 2020〜2100 の整数で入力してね。';
  return year;
}

/** BasicInfo フォームから対象月を読み取り、エラーなら文字列を返す */
export function readMonthFromBasicInfo(): number | string {
  const month = parseYenInput(readSelectDigits('#basic-info-month', 2));
  if (month === null) return '対象月を入力してね。';
  if (month < 1 || month > 12) return '対象月は 1〜12 の整数で入力してね。';
  return month;
}
